import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import type { RootState } from "../../../store/store";
import { LOGINPAGE } from "../../auth/routes";
import { ESTOQUE_PAGE } from "../routes";
import {
  getMassBalanceStoresApi,
  getStoreStockListApi,
  adjustStoreStockApi,
  StoreShort,
  StoreProductStock
} from "../api/massBalanceApi";

import { useGoBack } from "../../../hooks/useGoBack";

export default function CurrentStockPage() {
  const navigate = useNavigate();
  const goBack = useGoBack(ESTOQUE_PAGE());
  const token = useSelector((state: RootState) => state.auth.accessToken);

  const [stores, setStores] = useState<StoreShort[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [stocks, setStocks] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(15);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);

  // Adjustment Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedStock, setSelectedStock] = useState<any | null>(null);
  const [newValue, setNewValue] = useState(0);
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!token) {
      toast.error("Você precisa estar logado.");
      navigate(LOGINPAGE(), { replace: true });
    }
  }, [token, navigate]);

  useEffect(() => {
    const loadStores = async () => {
      if (!token) return;
      try {
        const data = await getMassBalanceStoresApi(token);
        setStores(data || []);
        if (data && data.length > 0) {
          setSelectedStoreId(data[0].id);
        }
      } catch (err: any) {
        toast.error(err.message || "Erro ao carregar lojas.");
      }
    };
    loadStores();
  }, [token]);

  const loadStock = async () => {
    if (!token || !selectedStoreId) return;
    try {
      setLoading(true);
      const res = await getStoreStockListApi(token, selectedStoreId, {
        query: searchQuery.trim() || undefined,
        page,
        limit,
      });
      setStocks(res.data || []);
      setTotal(res.meta?.total || 0);
    } catch (err: any) {
      toast.error(err.message || "Erro ao buscar estoque da loja.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedStoreId) {
      loadStock();
    } else {
      setStocks([]);
      setTotal(0);
    }
  }, [token, selectedStoreId, page, searchQuery]);

  const handleOpenAdjust = (stock: any) => {
    setSelectedStock(stock);
    setNewValue(stock.estoqueAtual);
    setReason("");
    setModalOpen(true);
  };

  const handleAdjustSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !selectedStoreId || !selectedStock) return;

    if (newValue < 0 || !reason.trim()) {
      toast.error("Preencha todos os campos corretamente.");
      return;
    }

    try {
      await adjustStoreStockApi(token, selectedStoreId, selectedStock.id, {
        newValue,
        reason: reason.trim(),
      });
      toast.success("Estoque corrigido com sucesso!");
      setModalOpen(false);
      loadStock();
    } catch (err: any) {
      toast.error(err.message || "Erro ao corrigir estoque.");
    }
  };

  const totalPages = Math.ceil(total / limit) || 1;

  if (!token) return null;

  return (
    <div className="estoqueLayout">
      <main className="estoqueMain">
        <div className="estoqueContainer">
          <EcoPageHeader
            crumbs={[{ label: "Estoque", onClick: () => navigate(ESTOQUE_PAGE()) }, { label: "Estoque atual" }]}
            title="Estoque atual por loja"
            subtitle="Saldo físico projetado de cada loja, com correção registrada e motivo de auditoria."
            onBack={goBack}
            backLabel="Voltar para Estoque"
          />

          <section className="estoqueControls" style={{ marginTop: 20 }}>
            <div className="estoqueFormGroup" style={{ minWidth: 200 }}>
              <select
                className="estoqueSelect"
                value={selectedStoreId}
                onChange={(e) => {
                  setSelectedStoreId(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">Selecione a Loja...</option>
                {stores.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nomeFantasia}
                  </option>
                ))}
              </select>
            </div>

            <input
              type="text"
              className="estoqueInput"
              placeholder="Buscar por descrição ou SKU..."
              value={searchQuery}
              disabled={!selectedStoreId}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
            />
          </section>

          {!selectedStoreId ? (
            <div style={{ textAlign: "center", padding: 40, color: "var(--eco-muted)" }}>
              Selecione uma loja para carregar os saldos de estoque.
            </div>
          ) : loading ? (
            <div style={{ textAlign: "center", padding: 40, color: "var(--eco-muted)" }}>Carregando dados...</div>
          ) : (
            <>
              <div className="estoqueTableWrapper">
                <table className="estoqueTable">
                  <thead>
                    <tr>
                      <th>EAN / SKU</th>
                      <th>Descrição do Produto</th>
                      <th>Qtd em Estoque (Projetado)</th>
                      <th style={{ width: 150, textAlign: "center" }}>Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stocks.length === 0 ? (
                      <tr>
                        <td colSpan={4} style={{ textAlign: "center", padding: 24, color: "var(--eco-muted)" }}>
                          Nenhum produto cadastrado com estoque para esta loja.
                        </td>
                      </tr>
                    ) : (
                      stocks.map((stock) => (
                        <tr key={stock.id}>
                          <td><strong>{stock.productId}</strong></td>
                          <td>{stock.descricao}</td>
                          <td>{stock.estoqueAtual}</td>
                          <td>
                            <div style={{ display: "flex", justifyContent: "center" }}>
                              <button
                                className="estoqueBtn estoqueBtn--secondary"
                                style={{ height: 32, padding: "0 12px" }}
                                onClick={() => handleOpenAdjust(stock)}
                              >
                                Corrigir Saldo
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="estoquePagination">
                <span>
                  Mostrando {stocks.length} de {total} itens
                </span>
                <div className="estoquePagination__btns">
                  <button
                    className="estoqueBtn estoqueBtn--secondary"
                    style={{ height: 32 }}
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Anterior
                  </button>
                  <span style={{ display: "inline-flex", alignItems: "center", padding: "0 12px" }}>
                    Página {page} de {totalPages}
                  </span>
                  <button
                    className="estoqueBtn estoqueBtn--secondary"
                    style={{ height: 32 }}
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Próxima
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      {/* Adjust Modal */}
      {modalOpen && selectedStock && (
        <div className="estoqueModalOverlay">
          <div className="estoqueModal">
            <header className="estoqueModal__header">
              <h2 className="estoqueModal__title">Corrigir Estoque Físico</h2>
              <button className="estoqueModal__close" onClick={() => setModalOpen(false)}>
                &times;
              </button>
            </header>

            <form onSubmit={handleAdjustSubmit}>
              <div className="estoqueModal__body">
                <div style={{ padding: 12, background: "var(--eco-ground)", borderRadius: 8, fontSize: 13, marginBottom: 8 }}>
                  <div>Produto: <strong>{selectedStock.descricao}</strong></div>
                  <div>EAN: {selectedStock.productId}</div>
                  <div>Estoque Atual: {selectedStock.estoqueAtual}</div>
                </div>

                <div className="estoqueFormGroup">
                  <label>Novo Saldo em Estoque *</label>
                  <input
                    type="number"
                    className="estoqueInput"
                    min={0}
                    value={newValue}
                    onChange={(e) => setNewValue(Math.max(0, Number(e.target.value)))}
                    required
                  />
                </div>

                <div className="estoqueFormGroup">
                  <label>Motivo do Ajuste (Auditoria) *</label>
                  <textarea
                    className="estoqueInput"
                    style={{ height: 80, padding: 8, resize: "none" }}
                    placeholder="Descreva o motivo desta correção manual..."
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    required
                    maxLength={500}
                  />
                </div>
              </div>

              <footer className="estoqueModal__footer">
                <button
                  type="button"
                  className="estoqueBtn estoqueBtn--secondary"
                  onClick={() => setModalOpen(false)}
                >
                  Cancelar
                </button>
                <button type="submit" className="estoqueBtn estoqueBtn--primary">
                  Confirmar Correção
                </button>
              </footer>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
